import type { LanguageId, Visibility } from '../shared/types'

/**
 * The terminal command that pulls a pasta back out again — the read half of
 * the `curl --data-binary` upload the README already documents.
 *
 * Two things decide what it says. The language, because fetching a PowerShell
 * script and fetching a CSV want different endings; and the visibility,
 * because a private pasta's /raw needs a bearer token and a command without
 * one would just 404 in a way that looks like the link is broken.
 */

/** The env var the README tells you to export. */
const TOKEN_VAR = 'PASTABIN_TOKEN'

/** Titles are user text; only spend one as a filename if it already looks like one. */
const FILENAME_SAFE = /^[A-Za-z0-9._-]{1,64}$/

export interface FetchCommand {
  /** The tool the command leads with. Doubles as the button label. */
  tool: string
  command: string
  /** True when the command executes the pasta rather than just saving it. */
  runs: boolean
  /** True when the command depends on $PASTABIN_TOKEN being set. */
  needsToken: boolean
}

export interface CommandTarget {
  id: string
  title: string | null
  language: LanguageId
  visibility: Visibility
}

export function fetchCommand(paste: CommandTarget, origin: string): FetchCommand {
  const url = `${origin}/raw/${paste.id}`
  const needsToken = paste.visibility === 'private'

  if (paste.language === 'powershell') {
    // Invoke-RestMethod hands back a string here because /raw is text/plain.
    // Feeding it to [scriptblock]::Create and invoking with & runs it in the
    // caller's scope without the `iex` habit that trips up wary shells.
    const headers = needsToken ? ` -Headers @{ Authorization = "Bearer $env:${TOKEN_VAR}" }` : ''
    return {
      tool: 'reheat',
      command: `& ([scriptblock]::Create((irm${headers} ${url})))`,
      runs: true,
      needsToken,
    }
  }

  const auth = needsToken ? ` -H "authorization: bearer $${TOKEN_VAR}"` : ''
  const curl = `curl -fsSL${auth} ${url}`

  if (paste.language === 'shell') {
    return { tool: 'curl', command: `${curl} | sh`, runs: true, needsToken }
  }
  if (paste.language === 'python') {
    return { tool: 'curl', command: `${curl} | python3 -`, runs: true, needsToken }
  }
  return { tool: 'curl', command: `${curl} -o ${filename(paste)}`, runs: false, needsToken }
}

/** What the tooltip says, so you can read the command before running it. */
export function describeCommand(command: FetchCommand): string {
  const what = command.runs
    ? 'Copy a command that fetches and runs this'
    : 'Copy a command that downloads this'
  const caveat = command.needsToken ? `\nNeeds $${TOKEN_VAR} — mint one at /tokens.` : ''
  return `${what}:\n${command.command}${caveat}`
}

function filename(paste: CommandTarget): string {
  const title = paste.title?.trim() ?? ''
  // Anything with a space or a shell metacharacter would need quoting, and a
  // half-quoted filename in someone's terminal is worse than a boring one.
  return FILENAME_SAFE.test(title) ? title : `${paste.id}.txt`
}
